import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { getSuggestionRanges } from '../utils/suggestionRanges'
import { pmPositionToStringOffset, stringOffsetToPmPosition } from '../utils/tiptapPlainText'

export const aiSuggestionPluginKey = new PluginKey('aiSuggestionDecorations')

/** Gợi ý AI: decoration inline (class .ai-suggestion). Meta refreshSuggestionDecorations khi chỉ đổi danh sách phrase. */
export const AISuggestionHighlight = Extension.create({
  name: 'aiSuggestionHighlight',
  addOptions() {
    return {
      getPhrases: (): string[] => [],
    }
  },
  addProseMirrorPlugins() {
    const extension = this
    const getPhrases = () => extension.options.getPhrases()
    const key = aiSuggestionPluginKey
    return [
      new Plugin({
        key,
        state: {
          init: () => ({ version: 0 }),
          apply(tr, prev) {
            if (tr.getMeta('refreshSuggestionDecorations')) {
              return { version: prev.version + 1 }
            }
            return prev
          },
        },
        props: {
          decorations(state) {
            void key.getState(state)
            const phrases = getPhrases()
            const text = state.doc.textBetween(0, state.doc.content.size, '\n', '\n')
            const ranges = getSuggestionRanges(text, phrases)
            const decos: Decoration[] = []
            for (const r of ranges) {
              const from = stringOffsetToPmPosition(state.doc, r.start)
              const to = stringOffsetToPmPosition(state.doc, r.end)
              if (from < to) {
                decos.push(Decoration.inline(from, to, { class: 'ai-suggestion' }))
              }
            }
            return DecorationSet.create(state.doc, decos)
          },
        },
      }),
    ]
  },
})

export const LegalDraftKeyboard = Extension.create({
  name: 'legalDraftKeyboard',
  addOptions() {
    return {
      getPhrases: (): string[] => [],
      onAcceptPhrase: (_phrase: string) => {},
      onDismissPhrase: (_phrase: string) => {},
    }
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const editor = this.editor
        const getPhrases = this.options.getPhrases as () => string[]
        const text = editor.getText({ blockSeparator: '\n' })
        const from = editor.state.selection.from
        const caret = pmPositionToStringOffset(editor.state.doc, from)
        const ranges = getSuggestionRanges(text, getPhrases())
        const hit = ranges.find((r) => caret >= r.start && caret <= r.end)
        if (hit) {
          this.options.onAcceptPhrase(hit.phrase)
          return true
        }
        return editor.commands.insertContent('\t')
      },
      Escape: () => {
        const editor = this.editor
        const getPhrases = this.options.getPhrases as () => string[]
        const text = editor.getText({ blockSeparator: '\n' })
        const from = editor.state.selection.from
        const caret = pmPositionToStringOffset(editor.state.doc, from)
        const ranges = getSuggestionRanges(text, getPhrases())
        const hit = ranges.find((r) => caret >= r.start && caret <= r.end)
        if (!hit) return false
        const fromPm = stringOffsetToPmPosition(editor.state.doc, hit.start)
        const toPm = stringOffsetToPmPosition(editor.state.doc, hit.end)
        const ok = editor.chain().focus().deleteRange({ from: fromPm, to: toPm }).run()
        if (ok) {
          this.options.onDismissPhrase(hit.phrase)
        }
        return ok
      },
    }
  },
})

export function createLegalDraftExtensions(opts: {
  getPhrases: () => string[]
  onAcceptPhrase: (phrase: string) => void
  onDismissPhrase: (phrase: string) => void
}) {
  return [
    StarterKit.configure({
      heading: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      listKeymap: false,
      blockquote: false,
      codeBlock: false,
      horizontalRule: false,
      bold: false,
      italic: false,
      strike: false,
      code: false,
      underline: false,
      link: false,
    }),
    AISuggestionHighlight.configure({
      getPhrases: opts.getPhrases,
    }),
    LegalDraftKeyboard.configure({
      getPhrases: opts.getPhrases,
      onAcceptPhrase: opts.onAcceptPhrase,
      onDismissPhrase: opts.onDismissPhrase,
    }),
  ]
}
